import { faCamera, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DefaultAvatar from 'components/global/DefaultAvatar';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import React, { useRef, useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import displayError from 'utils/displayError';
import displayNotification from 'utils/displayNotification';
import { storage } from 'utils/firebase';

const ProfileImageUpload = ({
  currentImage,
  defaultName,
  avatarColors,
  onImageUpload,
  onImageRemove,
  entityId, // user ID or organization ID
  disabled = false,
  className = 'profileSettingsAvatar',
  uploadPath = 'profile-assets',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

const handleUpload = async (file) => {
    try {
      setIsUploading(true);

      // Delete existing image first if it exists (in case of different file extension)
      if (currentImage) {
        try {
          // Extract file path from Firebase Storage URL
          const url = new URL(currentImage);
          const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);

          if (pathMatch) {
            const encodedPath = pathMatch[1];
            const filePath = decodeURIComponent(encodedPath);
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
          }
        } catch (storageError) {
          console.warn('Could not delete existing image from Firebase Storage:', storageError);
          // Continue with upload even if deletion fails
        }
      }

      // Generate filename using entity ID
      const fileExtension = file.name.split('.').pop();
      const fileName = `${entityId}.${fileExtension}`;
      const storageRef = ref(storage, `${uploadPath}/${fileName}`);

      // Upload file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Call parent callback
      if (onImageUpload) {
        await onImageUpload(downloadURL);
      }

      displayNotification({ message: 'Profile image updated successfully!' });
      setPreviewImage(null);
    } catch (error) {
      displayError({
        message: 'Failed to upload profile image. Please try again.',
        title: 'Upload Error'
      });
      setPreviewImage(null);
    } finally {
      setIsUploading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      displayError({
        message: 'Please select a valid image file.',
        title: 'Invalid File Type'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      displayError({
        message: 'File size must be less than 5MB.',
        title: 'File Too Large'
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    handleUpload(file);
  };

  const handleRemoveImage = async () => {
    try {
      // Delete file from Firebase Storage if it exists
      if (currentImage && entityId) {
        try {
          // Extract file extension from current image URL
          const url = new URL(currentImage);
          const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);

          if (pathMatch) {
            const encodedPath = pathMatch[1];
            const filePath = decodeURIComponent(encodedPath);
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
          }
        } catch (storageError) {
          console.warn('Could not delete from Firebase Storage:', storageError);
          // Continue with database update even if storage deletion fails
        }
      }

      // Call parent callback to update database
      if (onImageRemove) {
        await onImageRemove();
      }
      setPreviewImage(null);
      displayNotification({ message: 'Profile image removed successfully!' });
    } catch (error) {
      displayError({
        message: 'Failed to remove profile image. Please try again.',
        title: 'Remove Error'
      });
    }
  };

  const displayImage = previewImage || currentImage;

  return (
    <div className="position-relative d-flex flex-column align-items-center">
      <div className="position-relative mb-16">
        <div
          className={`d-flex justify-content-center rounded-circle flex-shrink-0 ${className}`}
          style={{ overflow: 'hidden' }}
        >
          {displayImage ? (
            <Image
              src={displayImage}
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
              alt="Profile"
            />
          ) : (
            <DefaultAvatar
              name={defaultName}
              className="profileDefaultSettingsAvatar"
              styleArr={avatarColors}
            />
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || isUploading}
        aria-label="Select image file"
      />

      {!disabled && (
      <div className="d-flex gap-8 justify-content-center">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          <FontAwesomeIcon icon={faCamera} className="me-8" />
          {currentImage ? 'Change' : 'Upload'}
        </Button>

        {currentImage && (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleRemoveImage}
          disabled={isUploading}
        >
          <FontAwesomeIcon icon={faTimes} className="me-8" />
          Remove
        </Button>
        )}
      </div>
      )}
    </div>
  );
};

export default ProfileImageUpload;
